<template>
    <div class="stock-period-picker" v-click-outside="close">
        <BaseButton
            variant="navyOutline"
            :custom-height="40"
            :custom-font-size="14"
            custom-class="ppv2-gap-2 !ppv2-px-4 smp-mockup-btn"
            :aria-expanded="isOpen ? 'true' : 'false'"
            @click="toggle"
        >
            <Calendar :size="16" />
            <span class="poppinsMedium">{{ appliedLabel }}</span>
            <ChevronDown :size="16" class="spp-chevron" :class="{ 'is-open': isOpen }" />
        </BaseButton>

        <transition name="spp-fade">
            <div
                v-if="isOpen"
                class="spp-popover ppv2-bg-white dark:ppv2-bg-dark"
                role="dialog"
                :aria-label="$t('stock.period.title')"
            >
                <div class="spp-body">
                    <div class="spp-presets">
                        <button
                            v-for="preset in presets"
                            :key="preset.key"
                            type="button"
                            class="spp-preset poppinsRegular ppv2-text-black"
                            :class="{ 'is-active': activePreset === preset.key }"
                            @click="selectPreset(preset.key)"
                        >
                            {{ preset.label }}
                        </button>
                    </div>
                    <div class="spp-calendar">
                        <DatePicker
                            :value="pickerValue"
                            range
                            inline
                            type="date"
                            :lang="calendarLang"
                            :disabled-date="isAfterToday"
                            :get-classes="getCalendarClasses"
                            :input-attr="{ style: 'display:none' }"
                            class="spp-datepicker"
                            @pick="onPick"
                            @input="onRangeInput"
                        />
                        <p class="spp-hint poppinsRegular">{{ $t('stock.period.pick_hint') }}</p>
                    </div>
                </div>
                <div class="spp-footer">
                    <span class="spp-preview poppinsMedium ppv2-text-black">{{ previewLabel }}</span>
                    <BaseButton
                        variant="purple"
                        :custom-height="36"
                        :custom-font-size="14"
                        custom-class="!ppv2-px-5"
                        :disabled="!tempFrom"
                        @click="apply"
                    >
                        {{ $t('stock.period.apply') }}
                    </BaseButton>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { Calendar, ChevronDown } from 'lucide-vue'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import moment from 'moment'
import BaseButton from '../../Shared/BaseButton.vue'
import { PERIOD_PRESETS, DEFAULT_PERIOD_PRESET, presetRange, matchPreset, formatPeriodLabel, toDateString } from './stockUtils'

const FR_LANG = {
    formatLocale: {
        firstDayOfWeek: 1,
        months: [
            'Janvier',
            'Février',
            'Mars',
            'Avril',
            'Mai',
            'Juin',
            'Juillet',
            'Août',
            'Septembre',
            'Octobre',
            'Novembre',
            'Décembre',
        ],
        monthsShort: ['Jan.', 'Fév.', 'Mar.', 'Avr.', 'Mai', 'Juin', 'Jui.', 'Aoû.', 'Sep.', 'Oct.', 'Nov.', 'Déc.'],
        weekdays: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
        weekdaysShort: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
        weekdaysMin: ['DI', 'LU', 'MA', 'ME', 'JE', 'VE', 'SA'],
    },
}

const EN_LANG = {
    formatLocale: {
        firstDayOfWeek: 1,
    },
}

function toPickerValue(from, to) {
    if (!from) return [null, null]
    return [moment(from, 'YYYY-MM-DD').toDate(), moment(to || from, 'YYYY-MM-DD').toDate()]
}

export default {
    name: 'StockPeriodPicker',
    components: { Calendar, ChevronDown, DatePicker, BaseButton },
    directives: {
        // Copie locale du motif `Statistics/components/DateRangePicker.vue`
        'click-outside': {
            bind(el, binding, vnode) {
                el.clickOutsideEvent = function (event) {
                    if (!(el === event.target || el.contains(event.target))) {
                        vnode.context[binding.expression](event)
                    }
                }
                document.body.addEventListener('click', el.clickOutsideEvent)
            },
            unbind(el) {
                document.body.removeEventListener('click', el.clickOutsideEvent)
            },
        },
    },
    props: {
        /** `{ from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' }` */
        value: {
            type: Object,
            default: () => presetRange(DEFAULT_PERIOD_PRESET),
        },
    },
    data() {
        return {
            isOpen: false,
            tempFrom: this.value.from,
            tempTo: this.value.to,
            // Valeur donnée au calendrier : toujours une plage complète. Le 1er clic n'y touche pas,
            // sinon le watcher `value` de CalendarRange réinitialise la demi-plage interne et le 2e clic
            // repartirait de zéro.
            pickerValue: toPickerValue(this.value.from, this.value.to),
            ignoreNextPick: false,
        }
    },
    computed: {
        locale() {
            return (this.$i18n && this.$i18n.locale) || 'fr'
        },
        calendarLang() {
            return this.locale === 'fr' ? FR_LANG : EN_LANG
        },
        presets() {
            return PERIOD_PRESETS.map((key) => ({ key, label: this.$t(`stock.period.${key}`) }))
        },
        activePreset() {
            return matchPreset({ from: this.tempFrom, to: this.tempTo })
        },
        appliedLabel() {
            return this.labelFor(this.value.from, this.value.to)
        },
        previewLabel() {
            return this.labelFor(this.tempFrom, this.tempTo)
        },
    },
    watch: {
        value: {
            deep: true,
            handler(next) {
                if (!this.isOpen && next) this.setTemp(next.from, next.to, true)
            },
        },
    },
    methods: {
        labelFor(from, to) {
            if (!from) return ''
            // « Aujourd'hui », « Hier », « 7 derniers jours » : le nom du preset ; semaine et mois : les dates.
            const preset = matchPreset({ from, to })
            if (preset && preset !== 'this_week' && preset !== 'this_month') {
                return this.$t(`stock.period.${preset}`)
            }
            return formatPeriodLabel(from, to, { locale: this.locale, t: (key, params) => this.$t(key, params) })
        },
        toggle() {
            if (this.isOpen) {
                this.close()
            } else {
                this.open()
            }
        },
        open() {
            this.setTemp(this.value.from, this.value.to, true)
            this.isOpen = true
        },
        close() {
            this.isOpen = false
        },
        setTemp(from, to, syncPicker) {
            this.tempFrom = from
            this.tempTo = to
            if (syncPicker) this.pickerValue = toPickerValue(from, to)
        },
        selectPreset(key) {
            const range = presetRange(key)
            this.setTemp(range.from, range.to, true)
        },
        isAfterToday(date) {
            return moment(date).isAfter(moment(), 'day')
        },
        /**
         * Clic sur une cellule (émis à chaque clic, y compris le 2e, juste après `input`) :
         * au 1er clic la plage devient ce seul jour ; au 2e on ignore, `onRangeInput` a déjà la plage.
         */
        onPick(date) {
            if (this.ignoreNextPick) {
                this.ignoreNextPick = false
                return
            }
            if (!date || this.isAfterToday(date)) return
            const day = toDateString(date)
            this.setTemp(day, day, false)
        },
        /** 2e clic : vue2-datepicker émet la plage complète. */
        onRangeInput(range) {
            if (!Array.isArray(range) || !range[0] || !range[1]) return
            const sorted = [moment(range[0]), moment(range[1])].sort((a, b) => a.valueOf() - b.valueOf())
            this.setTemp(toDateString(sorted[0]), toDateString(sorted[1]), true)
            this.ignoreNextPick = true
            this.$nextTick(() => {
                this.ignoreNextPick = false
            })
        },
        getCalendarClasses(date) {
            if (!this.tempFrom) return ''
            const classes = []
            if (moment(date).isSame(this.tempFrom, 'day')) classes.push('first')
            if (moment(date).isSame(this.tempTo || this.tempFrom, 'day')) classes.push('last')
            return classes.join(' ')
        },
        apply() {
            if (!this.tempFrom) return
            const range = { from: this.tempFrom, to: this.tempTo || this.tempFrom }
            this.$emit('input', range)
            this.$emit('change', range)
            this.close()
        },
        onKeydown(event) {
            if (this.isOpen && (event.key === 'Escape' || event.key === 'Esc')) {
                this.close()
            }
        },
    },
    mounted() {
        document.addEventListener('keydown', this.onKeydown)
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.onKeydown)
    },
}
</script>

<style scoped lang="scss">
// Boutons « période » / « Exporter » de la maquette : pilule blanche, bordure navy, survol gris #eef1f5.
// Surcharge locale (racine de BaseButton = élément scopé ici) : la variante navyOutline reste inchangée.
.smp-mockup-btn {
    background: #fff !important;
    border: 1px solid #021832 !important;
    border-radius: 999px !important;
    color: #021832 !important;
    padding: 0 20px !important;
    font-size: 13px !important;
    box-shadow: none !important;

    &:hover:not(:disabled) {
        background: #eef1f5 !important;
        border-color: #021832 !important;
        color: #021832 !important;
    }
}

.dark .smp-mockup-btn {
    background: transparent !important;
    border-color: rgba(255, 255, 255, 0.6) !important;
    color: #fff !important;
}

.stock-period-picker {
    position: relative;
    display: inline-block;
}

.spp-chevron {
    transition: transform 0.2s ease;

    &.is-open {
        transform: rotate(180deg);
    }
}

.spp-popover {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    z-index: 1000;
    width: 560px;
    max-width: calc(100vw - 32px);
    border-radius: 16px;
    box-shadow: 0 10px 40px rgba(2, 24, 50, 0.18);
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.spp-body {
    display: flex;
}

.spp-presets {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 16px 12px;
    border-right: 1px solid #f1eef5;
    min-width: 150px;
}

.spp-preset {
    text-align: left;
    padding: 8px 12px;
    font-size: 13px;
    border: 1.5px solid transparent;
    border-radius: 20px;
    background: transparent;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        color: #571f84;
        background: #f3eafa;
    }

    // Maquette : preset actif = fond #f3eafa, texte #571f84, graisse 600
    &.is-active {
        color: #571f84;
        background: #f3eafa;
        font-weight: 600;
        font-family: 'Poppins SemiBold', 'Poppins', sans-serif;
    }
}

.spp-calendar {
    flex: 1;
    padding: 14px 10px 8px;
}

.spp-hint {
    margin: 4px 8px 0;
    font-size: 11px;
    color: #9ca3af;
}

.spp-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    padding: 12px 20px;
    border-top: 1px solid #f1eef5;
}

.spp-preview {
    font-size: 13px;
}

.spp-datepicker {
    width: 100% !important;
}

::v-deep .mx-datepicker-main {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
}

::v-deep .mx-calendar {
    width: 100%;
}

::v-deep .mx-calendar-header {
    display: flex !important;
    align-items: center;
    justify-content: space-between !important;
    padding: 0 24px !important;
}

::v-deep .mx-btn-icon-left {
    order: 1 !important;
}

::v-deep .mx-calendar-header-label {
    order: 2 !important;
    font-family: 'Poppins SemiBold', 'Poppins', sans-serif;
    font-size: 13px;
    color: #021832;
}

::v-deep .mx-btn-icon-right {
    order: 3 !important;
}

::v-deep .mx-btn-icon-double-left,
::v-deep .mx-btn-icon-double-right {
    display: none !important;
}

::v-deep .mx-btn:hover {
    color: #7d2dbd !important;
}

::v-deep .mx-table-date th {
    color: #94a3b8 !important;
    text-transform: uppercase;
    font-size: 10px;
    font-weight: 700;
}

::v-deep .mx-table-date td {
    padding: 0 !important;
}

// `.cell` est un <td> : il doit RESTER une cellule de tableau (un `display: flex` ici empile les
// jours les uns sous les autres). Le centrage se fait sur le <div> interne rendu par vue2-datepicker.
::v-deep .mx-date-row .cell {
    border-radius: 0;
    height: 30px;
    position: relative;
    z-index: 1;
    font-size: 12px;
    color: #021832;
    text-align: center;
    vertical-align: middle;
}

::v-deep .mx-date-row .cell > div {
    height: 30px;
    line-height: 30px;
}

::v-deep .mx-date-row .cell.not-current-month {
    color: #c4c9d4;
}

::v-deep .mx-date-row .cell.disabled {
    color: #d1d5db;
    background: transparent;
    cursor: not-allowed;
}

::v-deep .mx-date-row .cell:hover:not(.disabled) {
    background: #f3eafa;
    color: #7d2dbd;
}

::v-deep .mx-date-row .cell.in-range,
::v-deep .mx-date-row .cell.hover-in-range {
    background: #ecdcfa;
    color: #3f1761;
}

::v-deep .mx-date-row .cell.active {
    background: #ecdcfa;
    color: #fff !important;
}

::v-deep .mx-date-row .cell.active.first {
    border-top-left-radius: 50%;
    border-bottom-left-radius: 50%;
}

::v-deep .mx-date-row .cell.active.last {
    border-top-right-radius: 50%;
    border-bottom-right-radius: 50%;
}

::v-deep .mx-date-row .cell.active::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 26px;
    height: 26px;
    transform: translate(-50%, -50%);
    background: #7d2dbd;
    border-radius: 50%;
    z-index: -1;
}

::v-deep .mx-date-row .cell.today:not(.active) {
    color: #7d2dbd;
    font-weight: 700;
}

.spp-fade-enter-active,
.spp-fade-leave-active {
    transition:
        opacity 0.15s,
        transform 0.15s;
}

.spp-fade-enter,
.spp-fade-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}

@media (max-width: 767px) {
    .spp-popover {
        left: 0;
        right: auto;
    }

    .spp-body {
        flex-direction: column;
    }

    .spp-presets {
        flex-direction: row;
        flex-wrap: wrap;
        border-right: 0;
        border-bottom: 1px solid #f1eef5;
        min-width: 0;
    }
}
</style>
